/**
 * Dialog to create a new DM or group channel.
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { createChannel, type StaffMember, type TeamChannel } from '@/api/team'
import { useAuthStore } from '@/store/auth'
import { useTeamStore } from '@/store/team'
import { notify } from '@/lib/notify'
import { cn } from '@/lib/utils'

interface Props {
  staff: StaffMember[]
  onClose: () => void
  onCreated: (channel: TeamChannel) => void
}

export function NewChannelDialog({ staff, onClose, onCreated }: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id)
  const onlineUsers = useTeamStore((s) => s.onlineUsers)
  const [tab, setTab] = useState<'dm' | 'group'>('dm')
  const [search, setSearch] = useState('')
  const [groupName, setGroupName] = useState('')
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [creating, setCreating] = useState(false)

  const filtered = staff
    .filter((s) => s.id !== currentUserId)
    .filter(
      (s) =>
        !search ||
        (s.display_name ?? '').toLowerCase().includes(search.toLowerCase()) ||
        s.email.toLowerCase().includes(search.toLowerCase()),
    )

  const handleDm = async (userId: string) => {
    setCreating(true)
    try {
      const ch = await createChannel({ kind: 'dm', members: [userId] })
      onCreated(ch)
    } catch (e: any) {
      notify.error(e.message || 'Failed to create conversation')
    } finally {
      setCreating(false)
    }
  }

  const handleGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) return
    setCreating(true)
    try {
      const ch = await createChannel({
        kind: 'group',
        name: groupName.trim(),
        members: selectedMembers,
      })
      onCreated(ch)
    } catch (e: any) {
      notify.error(e.message || 'Failed to create channel')
    } finally {
      setCreating(false)
    }
  }

  const toggleMember = (id: string) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>New conversation</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 p-0.5 rounded-md bg-surface-alt border border-hairline w-fit mb-3">
          <button
            onClick={() => setTab('dm')}
            className={cn(
              'px-3 py-1 text-xs rounded cursor-pointer',
              tab === 'dm' ? 'bg-primary/20 text-primary font-medium' : 'text-ink-500',
            )}
          >
            Direct Message
          </button>
          <button
            onClick={() => setTab('group')}
            className={cn(
              'px-3 py-1 text-xs rounded cursor-pointer',
              tab === 'group' ? 'bg-primary/20 text-primary font-medium' : 'text-ink-500',
            )}
          >
            Group
          </button>
        </div>

        {tab === 'group' && (
          <Input
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Channel name (e.g. ops-team)"
            className="h-11 sm:h-8 text-base sm:text-xs mb-2"
          />
        )}

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search staff..."
          className="h-11 sm:h-8 text-base sm:text-xs mb-2"
        />

        <div className="max-h-48 overflow-y-auto space-y-0.5" style={{ scrollbarWidth: 'none' }}>
          {filtered.map((s) => {
            const isSelected = selectedMembers.includes(s.id)
            const isOnline = onlineUsers.includes(s.id)
            return (
              <button
                key={s.id}
                disabled={creating}
                onClick={() => (tab === 'dm' ? handleDm(s.id) : toggleMember(s.id))}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-alt cursor-pointer text-left',
                  tab === 'group' && isSelected && 'bg-primary/10',
                )}
              >
                <span
                  className={cn(
                    'w-1.5 h-1.5 rounded-full shrink-0',
                    isOnline ? 'bg-success' : 'bg-ink-300',
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-ink-900 truncate">
                    {s.display_name || s.email.split('@')[0]}
                  </div>
                  <div className="text-[10px] text-ink-400 truncate">{s.email}</div>
                </div>
                {tab === 'group' && isSelected && (
                  <span className="text-[10px] text-primary font-medium">selected</span>
                )}
              </button>
            )
          })}
          {filtered.length === 0 && (
            <div className="text-xs text-ink-400 text-center py-4">No staff found</div>
          )}
        </div>

        {tab === 'group' && (
          <div className="flex justify-end pt-2 border-t border-hairline mt-2">
            <Button
              size="sm"
              disabled={creating || !groupName.trim() || selectedMembers.length === 0}
              onClick={handleGroup}
            >
              Create channel ({selectedMembers.length} members)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
